import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { api } from "../../common/setupApi";
import { selectLang } from "../lang/langSlice";

export const getChoices = createAsyncThunk(
  "choices/get",
  async (_, { rejectWithValue }) => {
    return api
      .get("squad-configuration/")
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectChoices(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

const initialState = {
  status: "idle",
  data: {},
  error: null,
};

const choicesSlice = createSlice({
  name: "choices",
  initialState,
  reducers: {
    addPlayerPosition: (state, action) => {
      state.data.position.push(action.payload);
    },
    setSliderPosition: (state, action) => {
      state.data.slider_position = action.payload;
    },
    setEmaPeriods: (state, action) => {
      state.data.ema_short = action.payload.short;
      state.data.ema_long = action.payload.long;
    },
    setDefaultLanguage: (state, action) => {
      state.data.language = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChoices.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(getChoices.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.data = action.payload;
      })
      .addCase(getChoices.rejected, (state, action) => {
        state.status = "rejected";
        state.error = action.payload;
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const choicesActions = choicesSlice.actions;

const _selectChoices = (state) => state.choices;

export const selectChoices = createSelector(
  [(state) => _selectChoices(state)],
  (choices) => {
    const data = choices.data;
    const status = choices.status;
    const error = choices.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading };
  }
);

// memo ok
export const selectSquadPositions = createSelector(
  [(state) => selectChoices(state).data.position || []],
  (position) => position.filter((d) => d !== null) || []
);

export const selectInjuriesTypologies = createSelector(
  [(state) => selectChoices(state), (state) => selectLang(state)],
  (choices, lang) => {
    const { data, status, error, isLoading } = choices;
    return {
      data: data.injury_type_id ? data.injury_type_id[lang] : [],
      status,
      error,
      isLoading,
    };
  }
);

export const selectDataDownloadTypes = createSelector(
  [(state) => selectChoices(state)],
  (choices) => {
    const { data, status, error, isLoading } = choices;
    return {
      data: data.export_type || [],
      status,
      error,
      isLoading,
    };
  }
);

// memo ok
export const selectSliderPosition = createSelector(
  [(state) => selectChoices(state).data.slider_position],
  (sliderPosition) => sliderPosition || 5.5
);

export const selectEmaPeriods = createSelector(
  [
    (state) => selectChoices(state).isLoading,
    (state) => selectChoices(state).status,
    (state) => selectChoices(state).data.ema_short,
    (state) => selectChoices(state).data.ema_long,
  ],
  (isLoading, status, emaShort, emaLong) => {
    return {
      isLoading,
      isFulfilled: status === "fulfilled",
      short: emaShort || 7,
      long: emaLong || 28,
    };
  }
);

export const selectDefaultLanguage = createSelector(
  [(state) => selectChoices(state).data.language],
  (defaultLanguage) => defaultLanguage || "en"
);

export const selectSchemaDetectorUrl = (state) =>
  selectChoices(state).data.detect_schema_url;

export const selectFileUploadUrl = (state) =>
  selectChoices(state).data.file_upload_url;

export default choicesSlice.reducer;
